import { filterAncestorsByYears, updateMissingBirthLocation } from '../../../shared/helpers/location_Helper'
import handleReducerSimpleSetter from '../../../shared/helpers/handleReducerSimpleSetter'
import getReducerHandler from '../../../shared/helpers/getReducerHandler'

const ACTIONS = Object.freeze({
  STATE_UPDATE: 'STATE_UPDATE',
  SET_SELECTED_PERSON: 'SET_SELECTED_PERSON',
  SET_MISSING_LOCATION: 'SET_MISSING_LOCATION',
  UPDATE_INITIAL_STATE: 'UPDATE_INITIAL_STATE',
  SET_HERITAGE_CONTENT: 'SET_HERITAGE_CONTENT',
  UPDATE_GENERATION_FILTERS: 'UPDATE_GENERATION_FILTERS',
  RESET_FILTERS: 'RESET_FILTERS',
  FILTER_ANCESTORS_BY_YEARS: 'FILTER_ANCESTORS_BY_YEARS',
  ADD_ANCESTORS: 'ADD_ANCESTORS',
  UPDATE_ANCESTOR_BIRTH: 'UPDATE_ANCESTOR_BIRTH',
  RESET_MISSING_ANCESTOR: 'RESET_MISSING_ANCESTOR',
  ADD_CUSTOM_COUNTRY: 'ADD_CUSTOM_COUNTRY',
  TOGGLE_ATF: 'TOGGLE_ATF',
  REMOVE_CUSTOM_COUNTRY: 'REMOVE_CUSTOM_COUNTRY',
  REMOVE_OLD_COUNTRY: 'REMOVE_OLD_COUNTRY',
})

const wcfInitialState = {
  noTreeDefaultRedirect: false,
  hasMappableTree: false,
  hasUpdatedInitialState: false,
  hasUpdatedAncestor: false,
  hasFamilyDataError: false,
  isAccessible: false,
  isAddTreeFlowShowing: false,
  tree: [],
  locationLookup: [],
  countryPercentages: [],
  countryCodeMatchedIds: [],
  filteredTree: [],
  views: {
    generations: {
      selectedAncestor: {},
      isPersonViewerShowing: false,
      isMissingLocationShowing: false,
      selectedFilterChip: 'all',
      selectedFilterStops: [1, 8],
    },
    timeline: {
      selectedAncestor: {},
      isPersonViewerShowing: false,
    },
    social: { content: {}, noDataFound: false },
    families: { content: {}, noDataFound: false },
    food: { content: {}, noDataFound: false },
    fun: { content: {}, noDataFound: false },
    traditions: { content: [], noDataFound: false },
    church: { content: [], noDataFound: false },
    myAncestors: { content: [], noDataFound: false },
  },
  userId: '',
}

/*
 * --- INDIVIDUAL ACTION HANDLERS ---
 * (Set multiple values & merge/processing logic only, otherwise use handleReducerSimpleSetter)
 */

const handleSetSelectedPerson = (state, action) => {
  const { which, ancestor, isPersonViewerShowing } = action.data
  const views = {
    ...state.views,
    [which]: {
      ...state.views[which],
      selectedAncestor: ancestor,
      isPersonViewerShowing,
    },
  }
  return { ...state, views }
}

const handleSetMissingLocation = (state, action) => {
  const { which, ancestor, isMissingLocationShowing } = action.data
  const views = {
    ...state.views,
    [which]: {
      ...state.views[which],
      selectedAncestor: ancestor,
      isMissingLocationShowing,
    },
  }
  return { ...state, views }
}

const handleUpdateInitialState = (state, action) => {
  const {
    noTreeDefaultRedirect,
    countryPercentages,
    countryCodeMatchedIds,
    tree,
    locationLookup,
    userId,
    hasUpdatedInitialState,
    hasMappableTree,
    hasFamilyDataError,
  } = action.data
  return {
    ...state,
    noTreeDefaultRedirect,
    countryPercentages,
    countryCodeMatchedIds,
    tree,
    filteredTree: tree,
    locationLookup,
    userId,
    hasUpdatedInitialState,
    hasMappableTree,
    hasFamilyDataError: !!hasFamilyDataError,
    isAddTreeFlowShowing: hasMappableTree ? state.isAddTreeFlowShowing : true,
  }
}

const handleSetHeritageContent = (state, action) => {
  const { which, content, noDataFound } = action.data
  const views = {
    ...state.views,
    [which]: { ...state.views[which], content, noDataFound },
  }
  return { ...state, views }
}

const handleUpdateGenerationFilters = (state, action) => {
  const { selectedFilterChip, selectedFilterStops, filteredTree } = action.data
  const generations = {
    ...state.views.generations,
    selectedFilterChip: selectedFilterChip || state.views.generations.selectedFilterChip,
    selectedFilterStops: selectedFilterStops || state.views.generations.selectedFilterStops,
  }
  return {
    ...state,
    views: { ...state.views, generations },
    filteredTree: filteredTree || state.filteredTree,
  }
}

const handleResetFilters = (state) => ({
  ...state,
  views: {
    ...state.views,
    generations: { ...state.views.generations, selectedFilterChip: 'all', selectedFilterStops: [1, 8] },
  },
  filteredTree: state.tree,
})

const handleFilterAncestorsByYears = (state, action) => ({
  ...state,
  filteredTree: filterAncestorsByYears(state.tree, state.filteredTree, action.data),
})

const handleAddAncestors = (state, action) => ({
  ...state,
  tree: state.tree.concat(action.data.ancestorsToAdd),
})

const handleUpdateAncestorBirth = (state, action) => {
  const idx = state.filteredTree.findIndex((p) => p.personId === action.data.personId)
  if (idx === -1) return state
  const updatedAncestor = updateMissingBirthLocation(state.filteredTree, action.data)
  const filteredTree = [...state.filteredTree]
  filteredTree[idx] = updatedAncestor
  return { ...state, filteredTree, hasUpdatedAncestor: true }
}

const handleResetMissingAncestor = (state) => ({ ...state, hasUpdatedAncestor: false })

const handleAddCustomCountry = (state, action) => ({
  ...state,
  countryPercentages: [...state.countryPercentages, { cc: action.data.cc, lab: action.data.lab }],
})

const handleToggleAtf = (state, action) => ({
  ...state,
  isAddTreeFlowShowing: !state.isAddTreeFlowShowing,
  hasUpdatedAncestor: !!action.data.updated,
})

const handleRemoveCustomCountry = (state, action) => ({
  ...state,
  countryPercentages: state.countryPercentages.filter((c) => c.cc !== action.data.cc),
})

const handleRemoveOldCountry = (state, action) => ({
  ...state,
  countryPercentages: state.countryPercentages.filter((c) => c.countryName !== action.data.countryName),
})

// --- Handlers registry ---
const handlers = {
  [ACTIONS.STATE_UPDATE]: handleReducerSimpleSetter,
  [ACTIONS.SET_SELECTED_PERSON]: handleSetSelectedPerson,
  [ACTIONS.SET_MISSING_LOCATION]: handleSetMissingLocation,
  [ACTIONS.UPDATE_INITIAL_STATE]: handleUpdateInitialState,
  [ACTIONS.SET_HERITAGE_CONTENT]: handleSetHeritageContent,
  [ACTIONS.UPDATE_GENERATION_FILTERS]: handleUpdateGenerationFilters,
  [ACTIONS.RESET_FILTERS]: handleResetFilters,
  [ACTIONS.FILTER_ANCESTORS_BY_YEARS]: handleFilterAncestorsByYears,
  [ACTIONS.ADD_ANCESTORS]: handleAddAncestors,
  [ACTIONS.UPDATE_ANCESTOR_BIRTH]: handleUpdateAncestorBirth,
  [ACTIONS.RESET_MISSING_ANCESTOR]: handleResetMissingAncestor,
  [ACTIONS.ADD_CUSTOM_COUNTRY]: handleAddCustomCountry,
  [ACTIONS.TOGGLE_ATF]: handleToggleAtf,
  [ACTIONS.REMOVE_CUSTOM_COUNTRY]: handleRemoveCustomCountry,
  [ACTIONS.REMOVE_OLD_COUNTRY]: handleRemoveOldCountry,
}

const wcfReducer = (state, action) => getReducerHandler(state, action, handlers)

export { wcfInitialState, wcfReducer, ACTIONS }
